/**
 * Workflow Diagram
 *
 *      ┌─────────────────────┐
 *      │   Props (inputs)    │
 *      │ children, open,     │
 *      │ variant, paddingTop,│
 *      │ sx                  │
 *      └───────────┬─────────┘
 *                  │
 *                  ▼
 *        ┌───────────────────────┐
 *        │   StyledDrawer        │
 *        │   (Material-UI base)  │
 *        └───────────┬───────────┘
 *                    │
 *      open ?        │       :       
 *   ┌───────┴───────┐ ┌───────────┐
 *   │ width = theme │ │ width=69px│
 *   │ .drawerWidth  │ │ (collapsed│
 *   │ (expanded)    │ │   state)  │
 *   └───────┬───────┘ └─────┬─────┘
 *           │               │
 *           ▼               ▼
 *   Apply drawer styles: width, paddingTop,
 *   backgroundColor, paper overrides + sx
 *           │
 *           ▼
 *      Render children
 */

import StyledDrawer from "./StyledDrawer.style";
import { drawerClasses } from "@mui/material"; // Needed for targeting MUI paper class

/**
 * Drawer Component
 * ----------------
 * A styled wrapper around Material-UI's Drawer.  
 * Dynamically adjusts width based on "open" state and applies consistent styling.
 *
 * Props:
 * - children: React nodes inside the drawer
 * - open: boolean → expanded or collapsed drawer
 * - variant: MUI Drawer variant (default = "permanent")
 * - paddingTop: space at the top (default = "inherit")
 * - sx: extra custom styles
 */
const Drawer = (props) => {
  const { children, open, variant = "permanent", paddingTop = "inherit", sx = {} } = props;

  return (
    <StyledDrawer
      variant={variant}
      open={open}
      sx={(theme) => {
        // If drawer is open → full width, else collapsed (69px)
        const width = open ? theme.drawerWidth : "69px";

        return {
          width,
          paddingTop,
          // Override MUI drawer "paper" element styles
          [`& .${drawerClasses.paper}`]: {
            width,
            paddingTop,
            backgroundColor: "background.paper",
          },
          ...sx, // Merge custom styles passed as prop
        };
      }}
    >
      {/* Render drawer contents */}
      {children}
    </StyledDrawer>
  );
};

export default Drawer;
